import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CardHeader, Container, Grid, MenuItem, TextField, Typography } from "@mui/material";
import MaterialTable from "material-table";
import Swal from "sweetalert2";

import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import axios from "axios";

const DEFAULT_DORMANT_MONTHS = 6;
const monthOptions = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthToNumber = (monthName) => monthOptions.findIndex((month) => month === monthName) + 1;
const numberToMonth = (monthNumber) => monthOptions[monthNumber - 1] || "Jan";

export const DormantCustomersAnalysisPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { state } = useLocation();
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);
    const defaultCountryId = Number(state?.countryId) || Number(identity?.user?.country_id);
    const currentDate = new Date();
    const currentMonthName = monthOptions[currentDate.getMonth()];
    const currentYear = currentDate.getFullYear();

    const [fromMonth, setFromMonth] = useState("Jan");
    const [fromYear, setFromYear] = useState(currentYear);
    const [toMonth, setToMonth] = useState(currentMonthName);
    const [toYear, setToYear] = useState(currentYear);
    const [dormantMonths, setDormantMonths] = useState(DEFAULT_DORMANT_MONTHS);

    const [countryId] = useState(defaultCountryId);
    const [firstTransactionYear, setFirstTransactionYear] = useState(currentYear);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);

    const getValidFirstTransactionYear = (value) => {
        const parsedYear = Number(value);
        return Number.isInteger(parsedYear) && parsedYear > 0 && parsedYear <= currentYear
            ? parsedYear
            : null;
    };

    const yearOptions = useMemo(() => {
        const startYear = Number.isInteger(firstTransactionYear)
            ? Math.min(firstTransactionYear, currentYear)
            : currentYear;

        return Array.from(
            { length: currentYear - startYear + 1 },
            (_, index) => currentYear - index
        );
    }, [currentYear, firstTransactionYear]);

    const selectedPeriodLabel = useMemo(
        () => `${fromMonth.toUpperCase()} ${fromYear} to ${toMonth.toUpperCase()} ${toYear} (Dormant Months: ${dormantMonths})`,
        [fromMonth, fromYear, toMonth, toYear, dormantMonths]
    );

    const fetchMonthlyDormantCustomerBreakdown = useCallback(async (customPeriod) => {
        if (!countryId) return;

        const requestedFromMonth = customPeriod.fromMonth;
        const requestedFromYear = customPeriod.fromYear;
        const requestedToMonth = customPeriod.toMonth;
        const requestedToYear = customPeriod.toYear;
        const requestedDormantMonths = Number(customPeriod.dormantMonths) || DEFAULT_DORMANT_MONTHS;

        setIsLoading(true);
        try {
            const response = await axios.get(
                process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${countryId}/monthly-dormant-customer-breakdown`,
                {
                    params: {
                        from_month: monthToNumber(requestedFromMonth),
                        from_year: requestedFromYear,
                        to_month: monthToNumber(requestedToMonth),
                        to_year: requestedToYear,
                        dormant_months: requestedDormantMonths,
                    },
                }
            );

            const payload = response?.data?.data || {};
            const validFirstTransactionYear = getValidFirstTransactionYear(payload.firstTransactionYear);
            const rows = (payload.monthlyDormantCustomerBreakdown || []).map((item) => ({
                month: item.month,
                year: item.year,
                month_year: item.period || `${numberToMonth(item.month)} ${item.year}`,
                dormant_end_users_count: item.dormantEndUsersCount || 0,
                dormant_dealers_count: item.dormantDealersCount || 0,
                total_dormant: item.totalDormant || 0,
                dormant_months: item.dormantMonths || requestedDormantMonths,
            }));

            setFirstTransactionYear(validFirstTransactionYear || currentYear);

            const summary = payload.totalSummary || {};
            const summaryRow = {
                month_year: "Total Summary",
                dormant_end_users_count: summary.dormantEndUsersCount || 0,
                dormant_dealers_count: summary.dormantDealersCount || 0,
                total_dormant: summary.totalDormant || 0,
                is_total: true,
            };

            setData([...rows, summaryRow]);
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setFirstTransactionYear(currentYear);

            const message = error?.response?.data?.message || "Unable to fetch monthly dormant customer breakdown.";
            Swal.fire({
                icon: "error",
                title: "Failed to Load Data",
                text: message,
                confirmButtonColor: "#fe7320",
            });
        } finally {
            setIsLoading(false);
        }
    }, [countryId, logout]);

    useEffect(() => {
        fetchMonthlyDormantCustomerBreakdown({
            fromMonth: "Jan",
            fromYear: currentYear,
            toMonth: currentMonthName,
            toYear: currentYear,
            dormantMonths,
        });
    }, [countryId, currentMonthName, currentYear, dormantMonths, fetchMonthlyDormantCustomerBreakdown]);

    useEffect(() => {
        if (yearOptions.length === 0) return;

        if (!yearOptions.includes(Number(fromYear))) {
            setFromYear(yearOptions[0]);
        }

        if (!yearOptions.includes(Number(toYear))) {
            setToYear(yearOptions[0]);
        }
    }, [fromYear, toYear, yearOptions]);

    const columns = [
        { title: "Month / Year", field: "month_year" },
        { title: "Dormant End Users", field: "dormant_end_users_count" },
        { title: "Dormant Dealers", field: "dormant_dealers_count" },
        { title: "Total Dormant", field: "total_dormant" },
        {
            title: "Actions",
            field: "action",
            sorting: false,
            render: (rowData) =>
                rowData.is_total ? (
                    ""
                ) : (
                    <Button
                        size="small"
                        color="primary"
                        variant="text"
                        sx={{ minWidth: "auto", px: 0.5 }}
                        onClick={() =>
                            navigate("/organizations/dormant-customers-analysis/list", {
                                state: {
                                    countryId,
                                    month: rowData.month,
                                    year: rowData.year,
                                    monthYear: rowData.month_year,
                                    dormantMonths: rowData.dormant_months || dormantMonths,
                                    totalDormant: rowData.total_dormant,
                                    endUsers: rowData.dormant_end_users_count,
                                    dealers: rowData.dormant_dealers_count,
                                },
                            })
                        }
                    >
                        View List
                    </Button>
                ),
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title="Dormant Customers Analysis"
                    icons={Icons}
                    columns={columns}
                    data={data}
                    isLoading={isLoading}
                    style={{ padding: "24px" }}
                    options={{
                        search: false,
                        filtering: false,
                        sorting: false,
                        paging: false,
                        toolbar: true,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        padding: "dense",
                        draggable: false,
                        rowStyle: (rowData) => ({
                            fontWeight: rowData.is_total ? 700 : 400,
                            backgroundColor: rowData.is_total ? "#f9fafb" : "#ffffff",
                        }),
                    }}
                    localization={{
                        body: {
                            emptyDataSourceMessage: "No data to display",
                        },
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <Grid container spacing={2} sx={{ px: 0.1, pb: 2 }}>
                                    <Grid item xs={12} sm={8}>
                                        <CardHeader
                                            sx={{ p: 0 }}
                                            title={
                                                <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                    Dormant Customers Analysis
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                        <Button color="error" onClick={() => navigate("/organizations")}>
                                            Back
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Box
                                    sx={{
                                        mb: 2.5,
                                        border: 1,
                                        borderColor: "#d7e1ed",
                                        borderRadius: 1,
                                        px: 2,
                                        py: 1.3,
                                        backgroundColor: "#ffffff",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 500, color: "#3a4a5a" }}>Overview Summary</Typography>
                                    <Typography sx={{ mt: 0.7, fontSize: "0.9rem", color: "#3a4a5a" }}>
                                        Selected Period: <strong>{selectedPeriodLabel}</strong>
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        columnGap: 1.5,
                                        rowGap: 1.2,
                                        pb: 2.5,
                                    }}
                                >
                                    <Box sx={{ flex: "0 0 145px", minWidth: 145 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="From Month"
                                            value={fromMonth}
                                            onChange={(event) => setFromMonth(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {monthOptions.map((month) => (
                                                <MenuItem key={month} value={month}>
                                                    {month}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 120px", minWidth: 120 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="From Year"
                                            value={fromYear}
                                            onChange={(event) => setFromYear(Number(event.target.value))}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {yearOptions.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 145px", minWidth: 145 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="To Month"
                                            value={toMonth}
                                            onChange={(event) => setToMonth(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {monthOptions.map((month) => (
                                                <MenuItem key={month} value={month}>
                                                    {month}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 120px", minWidth: 120 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="To Year"
                                            value={toYear}
                                            onChange={(event) => setToYear(Number(event.target.value))}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {yearOptions.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 130px", minWidth: 130 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Dormant Months"
                                            type="number"
                                            value={dormantMonths}
                                            onChange={(event) => setDormantMonths(Math.max(1, Number(event.target.value) || DEFAULT_DORMANT_MONTHS))}
                                            inputProps={{ min: 1 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: "0 0 96px", minWidth: 96 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            sx={{ height: "40px", whiteSpace: "nowrap" }}
                                            onClick={() =>
                                                fetchMonthlyDormantCustomerBreakdown({
                                                    fromMonth,
                                                    fromYear,
                                                    toMonth,
                                                    toYear,
                                                    dormantMonths,
                                                })
                                            }
                                            disabled={isLoading || !countryId}
                                        >
                                            Show
                                        </Button>
                                    </Box>
                                </Box>

                                <Typography sx={{ pb: 1.5, color: "#2e3d4f", fontWeight: 500 }}>Monthly Dormant Customer Breakdown</Typography>
                            </>
                        ),
                    }}
                />
            </Container>
        </div>
    );
};
